import type {
  ClientForBatch,
  CompliancePeriod,
  RecordComplianceCheckRequest,
  RecordComplianceChecksRequest,
  RecordReviewRequest,
} from "@repo/shared/types";
import type { QueryClient } from "@tanstack/query-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as complianceApi from "@/lib/api/compliance";
import { flowInstanceQueryKeys } from "@/lib/hooks/use-flow-instances/use-flow-instances";

/**
 * QL-21 — query keys for the two compliance review lists.
 *
 * Exported because the actions that change them do NOT live in this file: a
 * flow completing writes a `client_screening` row and adds a row to the flows
 * list, and it is completed from the flow wizard.
 */
export const complianceReviewKeys = {
  clients: ["compliance", "review", "clients"] as const,
  flows: ["compliance", "review", "flows"] as const,
};

/**
 * Refresh both compliance review lists.
 *
 * The app's default `staleTime` is 60s with `refetchOnWindowFocus: false`
 * (`providers/query-provider.tsx`), so without an explicit invalidation a list
 * visited within the last minute keeps serving cached rows — a flow completed
 * in the meantime simply does not appear until the browser is reloaded. Call
 * this from anything that records a screening or changes a flow's status.
 */
export function invalidateComplianceReview(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: complianceReviewKeys.clients }),
    queryClient.invalidateQueries({ queryKey: complianceReviewKeys.flows }),
  ]);
}

export function useComplianceViews() {
  return useQuery({
    queryKey: ["compliance-views"],
    queryFn: () => complianceApi.getViews(),
  });
}

export function useClientsForView() {
  return useMutation({
    mutationFn: (viewId: string) => complianceApi.getClientsForView(viewId),
  });
}

export function useBatchLookup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clients: ClientForBatch[]) => complianceApi.runBatchLookup(clients),
    // Every client the run covered gets a screening row and a new standing, so
    // the Clients review list this feeds is stale the moment the run returns.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: complianceReviewKeys.clients }),
  });
}

/** QL-21 — clients review list; `enabled` gates on the permission check. */
export function useClientsForReview(viewId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...complianceReviewKeys.clients, viewId],
    queryFn: () => complianceApi.getClientsForReview(viewId),
    enabled,
  });
}

/**
 * QL-b — the screening log for one client, for the review dialog's match list.
 * Only fetched while a client is actually being reviewed.
 */
export function useClientScreenings(clientId: string | null) {
  return useQuery({
    queryKey: ["compliance", "screenings", clientId],
    queryFn: () => complianceApi.getClientScreenings(clientId as string),
    enabled: Boolean(clientId),
  });
}

/** QL-21 — record a compliance verdict, then refresh the list it came from. */
export function useRecordReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, ...body }: { clientId: string } & RecordReviewRequest) =>
      complianceApi.recordReview(clientId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: complianceReviewKeys.clients }),
  });
}

/** QL-21 — flows review list; `enabled` gates on the permission check. */
export function useFlowsForReview(period: CompliancePeriod, enabled: boolean) {
  return useQuery({
    queryKey: [...complianceReviewKeys.flows, period],
    queryFn: () => complianceApi.getFlowsForReview(period),
    enabled,
  });
}

// ── Compliance review of completed advice ───────────────────────────────────

/**
 * The review history of one flow. Keyed by id and disabled when the dialog is
 * closed, so opening a row fetches its own log rather than every row's.
 */
export function useComplianceReviewLog(flowInstanceId: string | null) {
  return useQuery({
    queryKey: [...complianceReviewKeys.flows, "review-log", flowInstanceId],
    queryFn: () => complianceApi.getComplianceReviewLog(flowInstanceId as string),
    enabled: flowInstanceId !== null,
  });
}

/**
 * Every verdict appends a `compliance_review_*` row to `flow_instance_event`,
 * which the flow's process page renders as its activity log under
 * `["flowInstances", id, "process"]` — a key the compliance prefix does not
 * cover. Refresh it too, or an open process page shows the log without the
 * verdict until its own poll catches up.
 */
function invalidateAfterCheck(queryClient: QueryClient, flowInstanceIds: readonly string[]) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: complianceReviewKeys.flows }),
    ...flowInstanceIds.map((id) =>
      queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.process(id) }),
    ),
  ]);
}

/** Record a verdict on one completed flow, then refresh the list it shows in. */
export function useRecordComplianceCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      flowInstanceId,
      ...body
    }: { flowInstanceId: string } & RecordComplianceCheckRequest) =>
      complianceApi.recordComplianceCheck(flowInstanceId, body),
    onSuccess: (_result, { flowInstanceId }) => invalidateAfterCheck(queryClient, [flowInstanceId]),
  });
}

/** Record the same verdict on a selection of completed flows. */
export function useRecordComplianceChecks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordComplianceChecksRequest) => complianceApi.recordComplianceChecks(body),
    onSuccess: (_result, { flowInstanceIds }) => invalidateAfterCheck(queryClient, flowInstanceIds),
  });
}
